import { useEffect, useState } from "react";
import { BedDouble, MapPin, MessageCircle, Ruler } from "lucide-react";
import { MESSAGES, PROPERTIES, waLink } from "@/data/content";
import { FadeUp, SectionHead } from "./Reveal";

const Spec = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-2 text-sm text-ink/60">
    <Icon className="h-4 w-4 text-copper" strokeWidth={1.75} />
    {children}
  </span>
);

function PropertyCard({ p, delay = 0 }) {
  return (
    <FadeUp delay={delay} className="h-full">
      <article
        data-testid={`property-card-${p.ref}`}
        className="group relative flex h-full flex-col bg-white p-8 transition-transform duration-500 hover:-translate-y-2 md:p-10"
      >
        <div
          className="pointer-events-none absolute inset-0 border border-ink/10 transition-colors duration-500 group-hover:border-copper/50"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -end-2.5 -top-2.5 h-full w-full border border-copper/40 transition-transform duration-500 group-hover:-translate-x-1 group-hover:-translate-y-1"
          aria-hidden="true"
        />

        <span
          data-testid={`property-ref-${p.ref}`}
          className="font-display text-5xl font-semibold text-copper/35 transition-colors duration-500 group-hover:text-copper/70 md:text-6xl"
        >
          {p.ref}
        </span>

        <h3 className="mt-8 font-head text-2xl font-bold text-navy md:text-3xl">{p.type}</h3>
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <Spec icon={MapPin}>
            {p.city} · {p.district}
          </Spec>
          <Spec icon={Ruler}>{p.area} م²</Spec>
          <Spec icon={BedDouble}>{p.rooms} غرف</Spec>
        </div>
        {p.description ? (
          <p className="mt-4 text-sm leading-relaxed text-ink/60">{p.description}</p>
        ) : null}
        <p data-testid={`property-price-${p.ref}`} className="mt-6 font-head text-xl font-extrabold text-petrol">
          {p.price} <span className="text-sm font-semibold text-ink/50">ر.س</span>
        </p>

        <div className="mt-auto pt-9">
          <a
            href={waLink(MESSAGES.property(p.ref))}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`property-whatsapp-button-${p.ref}`}
            className="inline-flex w-full items-center justify-center gap-2.5 rounded-full border border-navy/25 px-6 py-3.5 font-head text-sm font-bold text-navy transition-[background-color,color,border-color] duration-300 hover:border-copper hover:bg-copper hover:text-navy"
          >
            <MessageCircle className="h-4 w-4" />
            استفسر عن العقار عبر واتساب
          </a>
        </div>
      </article>
    </FadeUp>
  );
}

export default function Properties() {
  const [items, setItems] = useState(PROPERTIES);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (Array.isArray(data) && data.length) setItems(data);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="properties" data-testid="properties-section" className="bg-cream py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <SectionHead eyebrow="WEEKLY SELECTION" eyebrowAr="مختارات أسبوعية" title="العقارات الأسبوعية المختارة" testId="properties-heading" />
        <FadeUp>
          <p className="-mt-8 mb-14 max-w-xl text-sm md:text-base text-ink/55">
            ثلاثة عقارات فقط، تُختار وتُحدَّث أسبوعيًا — ولكل عقار رقم مرجعي
            يجعل استفسارك يصل مباشرة إلى النقطة.
          </p>
        </FadeUp>

        <div className="grid gap-8 md:grid-cols-3 md:gap-10">
          {items.map((p, i) => (
            <PropertyCard key={p.ref} p={p} delay={i * 0.12} />
          ))}
        </div>
      </div>
    </section>
  );
}
